import React from 'react';
import { Link } from 'react-router-dom';
import { History, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Why the Compare tab has nothing to show yet.
 *
 * S7/U6: the tab used to dead-end. It was rendered `disabled` until an analysis completed, with
 * no inline hint about that condition, so the feature read as broken rather than gated. The tab is
 * now selectable and this notice carries the reason.
 *
 * Kept as its own component so both gate conditions share one presentation, and so S8 does not
 * have to extract it from `posture-analysis-app.tsx` later.
 */
export type CompareUnavailableReason = 'no-analysis' | 'experimental-off';

export interface CompareUnavailableNoticeProps {
  reason: CompareUnavailableReason;
  /** Returns to the capture step when Compare has nothing to show yet. */
  onBackToCapture?: () => void;
}

const COPY: Record<CompareUnavailableReason, { title: string; body: string }> = {
  'no-analysis': {
    title: 'Complete an analysis to compare',
    body: 'Capture or upload the views for this client and run the analysis. Once one is complete you can compare it against any saved analysis.',
  },
  'experimental-off': {
    title: 'Comparison is an experimental feature',
    body: 'Turn on experimental features to compare an analysis against a saved one.',
  },
};

export const CompareUnavailableNotice: React.FC<CompareUnavailableNoticeProps> = ({
  reason,
  onBackToCapture,
}) => {
  const copy = COPY[reason];

  return (
    <div
      data-testid="compare-unavailable-notice"
      className="flex flex-col items-center justify-center gap-3 h-64 rounded-lg border border-dashed bg-muted/20 px-6 text-center"
    >
      <History className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">{copy.title}</p>
        <p className="text-sm text-muted-foreground max-w-sm">{copy.body}</p>
      </div>
      {reason === 'no-analysis' && onBackToCapture && (
        <Button type="button" variant="outline" className="h-11" onClick={onBackToCapture}>
          Back to capture
        </Button>
      )}
      {reason === 'experimental-off' && (
        <Button asChild variant="outline" size="sm">
          <Link to="/settings">
            <Settings className="mr-2 h-4 w-4" aria-hidden="true" />
            Open Settings
          </Link>
        </Button>
      )}
    </div>
  );
};

export default CompareUnavailableNotice;
